import { DEAL_MODEL_SOURCE_CONTRACT_VERSION } from '../../api/dealModelContract'

export type DealModelSourceRecord = Record<string, unknown>

export type DealModelSemanticKind = 'money' | 'ratio' | 'percentage' | 'integer' | 'decimal' | 'count' | 'months' | 'days' | 'areaSqm' | 'boolean' | 'enum' | 'string' | 'date'

/** A system row: a value the model reads from a resolved property fact. */
export interface DealModelInputTemplate {
  key: string
  label: string
  description: string
  factPath: string
  /** Facts stored in pence convert to pounds once, at binding. */
  factUnit: 'pence' | 'pounds' | 'native'
  semanticKind: DealModelSemanticKind
}

export const DEAL_MODEL_INPUT_TEMPLATES: DealModelInputTemplate[] = [
  {
    key: 'purchasePrice',
    label: 'Purchase price',
    description: 'Resolved acquisition price from the marketed property price, with an optional property override.',
    factPath: 'listing.marketedPricePence',
    factUnit: 'pence',
    semanticKind: 'money',
  },
  {
    key: 'refurbCost',
    label: 'Refurbishment cost',
    description: 'Stored Standard all-in refurbishment budget. Missing remains distinct from a genuine zero.',
    factPath: 'diligence.refurbishment.scenarios.standard.costs.totalPence',
    factUnit: 'pence',
    semanticKind: 'money',
  },
  {
    key: 'readyPropertyValue',
    label: 'Ready property value',
    description: 'Resolved supported value after the proposed works are complete.',
    factPath: 'deal.readyPropertyValuePence',
    factUnit: 'pence',
    semanticKind: 'money',
  },
]

const CONTROL: Record<DealModelSemanticKind, string> = {
  money: 'money', ratio: 'number', percentage: 'percentage', integer: 'number', decimal: 'number',
  count: 'number', months: 'number', days: 'number', areaSqm: 'number', boolean: 'toggle',
  enum: 'select', string: 'readOnly', date: 'date',
}

const FORMAT: Record<DealModelSemanticKind, string> = {
  money: 'money', ratio: 'percentage', percentage: 'percentage', integer: 'integer', decimal: 'decimal',
  count: 'integer', months: 'integer', days: 'integer', areaSqm: 'decimal', boolean: 'boolean',
  enum: 'enum', string: 'text', date: 'date',
}

function semanticType(kind: DealModelSemanticKind): DealModelSourceRecord {
  if (kind === 'money') return { kind, nullable: false, minimum: '0' }
  if (kind === 'ratio' || kind === 'percentage' || kind === 'decimal') return { kind, nullable: false, minimum: '-1000', maximum: '1000' }
  return { kind, nullable: false }
}

function inputRecord(template: DealModelInputTemplate): DealModelSourceRecord {
  return {
    id: crypto.randomUUID(),
    key: template.key,
    label: template.label,
    description: template.description,
    targetPath: `inputs.${template.key}`,
    semanticType: semanticType(template.semanticKind),
    requiredForComputation: true,
    missingReasonCode: `${template.key}Missing`,
    resolution: {
      propertyOverrideKey: template.key,
      factPath: template.factPath,
      factUnit: template.factUnit,
    },
    simpleIntent: {
      control: CONTROL[template.semanticKind],
      editable: true,
      helpText: 'Resolved from the property fact. Money arrives in pence and converts to pounds once, at binding.',
    },
  }
}

function formulaRecord(key: string, expressionZen: string, kind: DealModelSemanticKind = 'money'): DealModelSourceRecord {
  return {
    id: crypto.randomUUID(),
    key,
    label: 'Calculated output',
    description: 'A GoRules/ZEN calculated value exposed to Deal Economics.',
    outputPath: `outputs.${key}`,
    semanticType: semanticType(kind),
    expressionZen,
    display: { simple: true, formulas: true, result: true, format: FORMAT[kind] },
  }
}

export function createDealModelSource(input: {
  logicalModelId: string
  modelKey: string
  family: string
  name: string
  description: string
}): DealModelSourceRecord {
  const purchasePrice = inputRecord(DEAL_MODEL_INPUT_TEMPLATES[0]!)
  // Money arithmetic always declares its GBP round point.
  const output = formulaRecord('headlineValue', 'round(inputs.purchasePrice, 2)')
  const outputId = String(output.id)
  const outputPath = String(output.outputPath)
  return {
    contractVersion: DEAL_MODEL_SOURCE_CONTRACT_VERSION,
    logicalModelId: input.logicalModelId,
    modelKey: input.modelKey,
    modelKind: 'dealModel',
    verdictContract: 'none',
    family: input.family,
    semanticVersion: '0.1.0',
    metadata: {
      label: input.name,
      description: input.description || 'User-authored Deal Model.',
      notes: ['Created in the Auction Hub Deal Model composer. All calculations are GoRules/ZEN expressions and all money is GBP.'],
    },
    resolutionContract: {
      runtimePrecedence: ['propertyOverride', 'allowedUserSetting', 'canonicalFact', 'modelFallback', 'unknown'],
      blockParameters: 'compileTimeExpansionOnly',
      provenance: 'exactlyOneRecordPerDeclaredInputInSourceOrder',
    },
    inputs: [purchasePrice],
    groups: [{
      id: crypto.randomUUID(),
      key: 'modelLedger',
      label: 'Model outputs',
      description: 'Ordered outputs presented by this Deal Model.',
      items: [{ kind: 'row', id: outputId }],
    }],
    rows: [output],
    blockInstances: [],
    modelDependencies: [],
    checks: [],
    diagnosticContracts: [],
    outcomes: { main: null, card: [] },
    scenarios: [],
    solverObjectives: [],
    outputs: { resultPaths: [outputPath], kpiRowIds: [outputId] },
    fixtures: [],
  }
}
